/**
 * Helpers for listing and selecting local webcam devices (getUserMedia).
 */

const FACING_USER_PATTERNS = /\b(front|user|facetime|selfie|integrated|built[- ]?in)\b/i;
const FACING_ENVIRONMENT_PATTERNS = /\b(back|rear|environment|world|external)\b/i;

export const isMobileUserAgent = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const inferFacingFromLabel = (label = '') => {
  const text = String(label || '').trim();
  if (!text) return null;
  if (FACING_ENVIRONMENT_PATTERNS.test(text)) return 'environment';
  if (FACING_USER_PATTERNS.test(text)) return 'user';
  return null;
};

export const formatCameraLabel = (device, index = 0) => {
  const trimmed = String(device?.label || '').trim();
  if (trimmed) return trimmed;

  const facing = device?.facingHint;
  if (facing === 'user') return 'Front camera';
  if (facing === 'environment') return 'Back camera';
  return `Camera ${index + 1}`;
};

export const mapVideoInputDevices = (rawDevices = []) =>
  rawDevices
    .filter((device) => device?.kind === 'videoinput' && device.deviceId)
    .map((device, index) => {
      const facingHint = inferFacingFromLabel(device.label);
      return {
        deviceId: device.deviceId,
        label: formatCameraLabel({ ...device, facingHint }, index),
        facingHint,
      };
    });

export const enumerateVideoInputDevices = async () => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return [];
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return mapVideoInputDevices(devices);
};

export const pickDefaultVideoDeviceId = (devices = [], preferFacing = 'user') => {
  if (!devices.length) return '';
  const preferred = devices.find((device) => device.facingHint === preferFacing);
  return preferred?.deviceId || devices[0].deviceId;
};

/** Prefer active selection, then enrollment session choice, then system default. */
export const resolveVideoDeviceId = (
  devices = [],
  { currentId = '', preferredId = '' } = {}
) => {
  if (!devices.length) return '';
  if (currentId && devices.some((device) => device.deviceId === currentId)) {
    return currentId;
  }
  if (preferredId && devices.some((device) => device.deviceId === preferredId)) {
    return preferredId;
  }
  return pickDefaultVideoDeviceId(devices, 'user');
};

export const findOppositeFacingDevice = (devices = [], currentDeviceId = '') => {
  const current = devices.find((device) => device.deviceId === currentDeviceId);
  if (!current?.facingHint) return null;

  const targetFacing = current.facingHint === 'user' ? 'environment' : 'user';
  return devices.find(
    (device) => device.deviceId !== currentDeviceId && device.facingHint === targetFacing
  );
};

export const getNextDeviceId = (devices = [], currentDeviceId = '') => {
  if (!devices.length) return '';
  const currentIndex = devices.findIndex((device) => device.deviceId === currentDeviceId);
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % devices.length;
  return devices[nextIndex]?.deviceId || devices[0].deviceId;
};

/**
 * @param {string} deviceId
 * @param {{ mobile?: boolean, facingMode?: 'user' | 'environment' }} [options]
 */
export const buildVideoConstraints = (deviceId = '', options = {}) => {
  const mobile = Boolean(options.mobile);
  const facingMode = options.facingMode === 'environment' ? 'environment' : 'user';

  const base = mobile
    ? {
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        facingMode,
      }
    : {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };

  if (mobile) {
    if (deviceId) {
      return {
        width: base.width,
        height: base.height,
        deviceId: { ideal: deviceId },
      };
    }
    return base;
  }

  // Desktop: never mix facingMode with deviceId — browsers keep the first matching camera.
  if (deviceId) {
    return {
      ...base,
      deviceId: { exact: deviceId },
    };
  }

  return {
    ...base,
    facingMode: 'user',
  };
};

export const getOppositeFacingMode = (facingMode = 'user') =>
  facingMode === 'environment' ? 'user' : 'environment';

export const readStreamFacingMode = (stream) => {
  const track = stream?.getVideoTracks?.()?.[0];
  const settings = track?.getSettings?.();
  const facing = settings?.facingMode;
  return facing === 'user' || facing === 'environment' ? facing : null;
};

export const shouldMirrorVideo = (facingMode) => facingMode !== 'environment';
